import { BORROWED_TIME, GULLET_JOB, LAST_ORDERS, ONE_ARMED_BANDITS, THE_COLLECTION, WET_PAPER } from './ids';
import type { RewardLine } from './types';

/**
 * Stamps for the story jobs (docs/MISSIONS.md §Replays): every finished job earns one of three rubber
 * stamps in the little black book, judged on the job's OWN bonus conditions — nothing new to learn:
 *
 *   1 DONE         finished.
 *   2 DONE CLEAN   earned the job's bonus (no scratch, quick, no shots…).
 *   3 DONE PROPER  the bonus and one thing more (stayed at a low wanted level, the top bonus, a par time).
 *
 * Inked copper, steel and brass on the page. The best stamp and the best time per job live in the save
 * (`SaveData.jobRecords`). A replay from the book pays only the bonus part (`replayLines`): the base fee was
 * paid the first time, so playing a job again is never a money tap and never moves the story.
 */

export type MedalTier = 0 | 1 | 2 | 3;

/** Stamp words (the margin of the book, the result sheet). */
export const MEDAL_STAMP: Record<Exclude<MedalTier, 0>, string> = { 1: 'DONE', 2: 'DONE CLEAN', 3: 'DONE PROPER' };
/** The same in a sentence ("New best: Done proper"). */
export const MEDAL_NAME: Record<Exclude<MedalTier, 0>, string> = { 1: 'Done', 2: 'Done clean', 3: 'Done proper' };

/** What a finished run looked like (worked out by the runner when the outro ends). */
export interface RunFacts {
  /** The job's full payout (the base line first, then bonuses / penalties). */
  lines: readonly RewardLine[];
  vars: Readonly<Record<string, number | string | boolean>>;
  /** Seconds of play from the end of the intro to the last objective (checkpoint retries included). */
  time: number;
  /** Highest wanted level while the job ran. */
  maxHeat: number;
}

export interface MedalRule {
  /** Plain text: what DONE CLEAN takes. */
  clean: string;
  /** Plain text: what DONE PROPER takes (on top of clean). */
  proper: string;
  isClean(r: RunFacts): boolean;
  isProper(r: RunFacts): boolean;
}

/** A bonus line was paid (anything positive after the base fee). */
const bonusPaid = (r: RunFacts): boolean => r.lines.slice(1).some((l) => l.amount > 0);
const num = (r: RunFacts, k: string): number => (typeof r.vars[k] === 'number' ? (r.vars[k] as number) : 0);

/** Par for The Gullet Job's DONE PROPER: the big one, start to finish, in under eight minutes. */
export const GULLET_PAR = 480;

export const MEDAL_RULES: Readonly<Record<string, MedalRule>> = {
  [BORROWED_TIME]: {
    clean: 'Deliver the Vesper without a scratch.',
    proper: 'Not a scratch, and never above wanted level 1.',
    isClean: bonusPaid,
    isProper: (r) => r.maxHeat <= 1,
  },
  [WET_PAPER]: {
    clean: 'Deliver with more than a minute on the clock.',
    proper: 'Deliver with more than two and a half minutes left — still dry.',
    isClean: (r) => num(r, 'timeLeftAtEnd') > 60,
    isProper: (r) => num(r, 'timeLeftAtEnd') > 150,
  },
  [GULLET_JOB]: {
    clean: 'Get away before DS Holloway joins the chase.',
    proper: `No Holloway, and home in under ${GULLET_PAR / 60} minutes.`,
    isClean: (r) => !r.vars.escalated,
    isProper: (r) => r.time < GULLET_PAR,
  },
  [ONE_ARMED_BANDITS]: {
    clean: 'Do the van with the bat (five good swings).',
    proper: 'The bat, and never above wanted level 1.',
    isClean: (r) => num(r, 'batHits') >= 5,
    isProper: (r) => r.maxHeat <= 1,
  },
  [THE_COLLECTION]: {
    clean: 'Back with more than two and a half minutes to spare.',
    proper: 'Early, and never above wanted level 1.',
    isClean: bonusPaid,
    isProper: (r) => r.maxHeat <= 1,
  },
  [LAST_ORDERS]: {
    clean: 'Not a shot fired.',
    proper: 'No shots, and never above wanted level 2.',
    isClean: (r) => !r.vars.fired,
    isProper: (r) => r.maxHeat <= 2,
  },
};

/** The stamp a finished run earns (0 for jobs without stamps: street jobs, exports, activities). */
export function medalFor(id: string, r: RunFacts): MedalTier {
  const rule = MEDAL_RULES[id];
  if (!rule) return 0;
  if (!rule.isClean(r)) return 1;
  return rule.isProper(r) ? 3 : 2;
}

/**
 * A replay's tally: the base fee shows at £0 ("paid the first time"), bonuses pay as usual, and penalty
 * lines are dropped (a replay never costs money). Bonus only — no money tap.
 */
export function replayLines(full: readonly RewardLine[]): RewardLine[] {
  const [base, ...rest] = full;
  const out: RewardLine[] = [{ label: `${base?.label ?? 'Payment'} — paid the first time`, amount: 0 }];
  for (const l of rest) if (l.amount > 0) out.push({ ...l });
  return out;
}

/** One job's record in the save. */
export interface JobRecord {
  /** Best stamp (1..3). */
  medal: MedalTier;
  /** Best time in seconds (null: none recorded — e.g. finished before stamps existed). */
  best: number | null;
  /** Finished runs, first time and replays. */
  runs: number;
}

/** Fold a finished run into a job's record. `medalUp` / `timeUp` say whether it beat the old one. */
export function recordRun(old: JobRecord | undefined, medal: MedalTier, time: number): { record: JobRecord; medalUp: boolean; timeUp: boolean } {
  const t = Math.max(0, Math.round(time * 10) / 10);
  const medalUp = medal > (old?.medal ?? 0);
  const timeUp = old?.best == null || t < old.best;
  return {
    record: { medal: (Math.max(old?.medal ?? 0, medal) as MedalTier), best: timeUp ? t : old!.best, runs: (old?.runs ?? 0) + 1 },
    medalUp,
    timeUp,
  };
}

/** "4:07" */
export function formatRunTime(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * The line under the stamp on the result sheet: "New best: Done proper · 4:07", "New best time: 3:58
 * (best stamp: Done clean)" or "Best: Done proper · 3:40".
 */
export function bestNote(medal: MedalTier, time: number, res: { record: JobRecord; medalUp: boolean; timeUp: boolean }, first: boolean): string {
  const rec = res.record;
  const bestTime = rec.best !== null ? ` · ${formatRunTime(rec.best)}` : '';
  if (first) return `First time: ${MEDAL_NAME[(medal || 1) as 1]} · ${formatRunTime(time)}`;
  if (res.medalUp) return `New best: ${MEDAL_NAME[rec.medal as 1]}${res.timeUp ? ` · ${formatRunTime(time)}` : bestTime}`;
  if (res.timeUp) return `New best time: ${formatRunTime(time)} (best stamp: ${MEDAL_NAME[rec.medal as 1]})`;
  return `Best: ${MEDAL_NAME[rec.medal as 1]}${bestTime}`;
}
